// Shared formatting/label helpers for the My Case views. The backend returns
// raw CRM status codes on purpose (the frontend owns plain language).

// Codes we can label confidently. Anything unknown passes through raw rather
// than guessing a meaning. TODO: confirm the full status code table with the
// CRM team and extend — especially RE, which appears in real data on
// components that were replaced by a newer active twin; "Not active" is the
// safe umbrella until the CRM team confirms the exact meaning.
const STATUS_LABELS = {
  NEW: "New",
  AC: "Active",
  CP: "Finished",
  NA: "Not started",
  RE: "Not active",
  DS: "Stopped",
  new: "New",
};

export const statusLabel = (code) => {
  if (!code) return null;
  return STATUS_LABELS[code] || String(code);
};

// Calm tone per status: positive/active -> jade, new/waiting -> marigold,
// anything else a readable grey (an unexpected code still renders).
export const toneForStatus = (code) => {
  const c = String(code || "").toUpperCase();
  if (["AC", "CP", "ACTIVE", "COMPLETED", "ENGAGED"].includes(c)) {
    return {bg: "#E6F2EF", text: "#005B4C", dot: "#007864"};
  }
  if (["NEW", "NA", "PENDING"].includes(c)) {
    return {bg: "#FFF3E3", text: "#9A5300", dot: "#FF9425"};
  }
  return {bg: "#EEF0F6", text: "#2B2F3A", dot: "#5B6172"};
};

// "YYYY-MM-DD..." -> "Jul 27, 2026". Field-wise local parse on purpose:
// `new Date("YYYY-MM-DD")` is UTC midnight and renders as the PREVIOUS day
// in every US timezone (the watch-history date bug).
export const formatDate = (s) => {
  if (!s || !/^\d{4}-\d{2}-\d{2}/.test(String(s))) return null;
  const [y, m, d] = String(s).slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// Decimal hours -> plain words: 1.25 -> "1 hour 15 minutes", 0.5 ->
// "30 minutes", 0 -> "0 minutes". Reads better than "1.25h" at any literacy
// level.
export const formatHours = (hours) => {
  // Number(null) is 0, so absent values need their own check.
  if (hours === null || hours === undefined || hours === "") return null;
  const n = Number(hours);
  if (!Number.isFinite(n) || n < 0) return null;
  const totalMinutes = Math.round(n * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h === 0) return `${m} minute${m === 1 ? "" : "s"}`;
  const hPart = `${h} hour${h === 1 ? "" : "s"}`;
  return m === 0 ? hPart : `${hPart} ${m} minute${m === 1 ? "" : "s"}`;
};

// --- Plain-language labels for DHW component/activity codes -----------------
// Sources: ET-Lookup-Values-06.25.26.xlsx (R_ACTIVITY sheet). Participants
// must never see raw codes (JST, OTH_CMT, ...) — same plain-language rule as
// the ABAWD/JSAP collapse (DECISIONS 08-10). Labels are the official DHW
// values, reworded only where the official text is itself jargon.
const COMPONENT_LABELS = {
  EMP: "Employment",
  WE: "Work Experience",
  COM: "Community Service",
  SJS: "Job Search",
  TRA: "Training",
  VOL: "Volunteering",
  OTH: "Other activities",
  JST: "Job Search Training",
  BSI: "Basic Skills and Education",
  CTE: "Career and Technical Training",
};

// Keyed by the ACTIVITY part of the code ("SJS_JS" -> "JS").
const ACTIVITY_LABELS = {
  UEM: "Working at a job",
  PRI: "Working at a subsidized job",
  PUB: "Working at a subsidized job",
  SE: "Self-employment",
  WE: "Work experience",
  COM: "Community service",
  JS: "Job search",
  VOL: "Volunteering",
  VOC: "Vocational training",
  TJS: "Job skills training",
  PRE: "Getting ready for work",
  E19: "School or classes",
  GED: "GED classes",
  E20: "School or classes",
  DED: "Approved break from activities",
  MED: "Medical",
  CTE: "Career and technical classes",
  JRT: "Job readiness class",
  OJR: "Training videos",
  ESL: "English classes",
  ABE: "Adult basic education",
  RAP: "Apprenticeship",
  PAP: "Pre-apprenticeship",
  OTH: "Other activity",
  INT: "Internship",
  CMT: "Meeting with your case manager",
};

// CM-entered names range from genuinely useful ("Resume Development") to
// jargon/PII ("Joseph Dionne - JST", "0001538900"). Junk = codes, long
// numbers, or nothing left but a person-name once those are stripped.
const CODE_TOKEN =
  /\b(JST|OJR|JRT|SJS|CETA|CMT|ABAWD|JSAP|EWS|E&T|WP|IRP|TS)\b/gi;
const isJunkName = (name) => {
  if (!name) return true;
  if (/\d{5,}/.test(name)) return true;
  const stripped = name
    .replace(CODE_TOKEN, "")
    .replace(/\b(activity|activities)\b/gi, "")
    .replace(/[-–:,.]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!stripped) return true;
  // Real activity titles contain activity vocabulary; a capitalized remainder
  // without any of it ("Joseph Dionne") is a person's name -> junk.
  const VOCAB =
    /\b(job|search|work|working|training|class|classes|course|service|community|resume|develop|skill|skills|education|ged|esl|video|videos|interview|meeting|plan|employ|volunteer|apprentice|orientation|weekly|online|school|readiness|internship|medical)\w*\b/i;
  // Case-insensitive: "JANE DOE" and "jane doe" are person names too.
  if (/^[A-Za-z]+( [A-Za-z]+){1,2}$/.test(stripped) && !VOCAB.test(stripped)) {
    return true;
  }
  return false;
};

// Component headings are participant-safe by construction: only our own
// plain-language labels render. Real DHW component codes are a fixed lookup
// set, so an unknown code is a data anomaly, and prod CM-typed component
// names are usually the participant's own name; no heuristic filter is
// trusted with a heading. (CM wording can still surface as an activity
// NOTE via activityDisplay, which does filter.)
export const componentLabel = (component) => {
  const code = String(component?.component_code || "").toUpperCase();
  return COMPONENT_LABELS[code] || "Activity group";
};

// Row title = plain label for the code; the CM's wording survives as a
// secondary note only when it is meaningful and adds something.
export const activityDisplay = (activity) => {
  const code = String(activity?.activity_code || "");
  const suffix = code.includes("_")
    ? code.slice(code.lastIndexOf("_") + 1)
    : code;
  const label = ACTIVITY_LABELS[suffix.toUpperCase()] || null;
  const name = activity?.name || null;
  const meaningfulName = name && !isJunkName(name) ? name : null;
  if (label) {
    return {
      title: label,
      note:
        meaningfulName && meaningfulName.toLowerCase() !== label.toLowerCase()
          ? meaningfulName
          : null,
    };
  }
  return {title: meaningfulName || "Activity", note: null};
};

// --- Date-window classification ---------------------------------------------
// CMs build PHASED plans: e.g. job search at 40h/wk for two weeks, then a
// second row at 8h/wk from then on (38 of 164 planned participants, 08-17).
// All rows are status-Active; only the row whose window covers TODAY is the
// current ask. Local date, field-wise, to avoid the UTC previous-day bug.
export const localTodayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export const activityWindow = (activity, today = localTodayISO()) => {
  const begin = String(activity?.begin_date || "").slice(0, 10);
  const end = String(activity?.end_date || "").slice(0, 10);
  if (begin && begin > today) return "future";
  if (end && end < today) return "expired";
  return "current";
};
